import { useCallback } from 'react';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { displayNameFor, queueFirstLoginWelcome } from '@/lib/login-welcome';
import type { AuthUser } from '@/types/auth.types';

/**
 * The one place that greets someone after a sign-in, whatever the method.
 *
 * A first sign-in gets the welcome dialog; everyone else, including anyone
 * talking to a backend that does not send `isFirstLogin` yet, gets a single
 * "welcome back" toast.
 */
export const useLoginWelcome = () => {
  const { t } = useApp();
  const { toast } = useToast();

  return useCallback(
    (user: Pick<AuthUser, 'id' | 'name' | 'username'> | null | undefined, isFirstLogin?: boolean) => {
      if (isFirstLogin === true && user?.id) {
        queueFirstLoginWelcome(String(user.id));
        return;
      }

      const name = displayNameFor(user);
      toast({ title: name ? t.welcomeBack.replace('{name}', name) : t.loginSuccess });
    },
    [t, toast],
  );
};
